import api from './api';

class IconService {
  constructor() {
    this.iconCache = new Set(); // URLs that have been successfully loaded
    this.loadingIcons = new Set(); // URLs currently being loaded
    this.failedIcons = new Set(); // URLs that have permanently failed
    this.coinDataCache = new Map(); // Cache for coin data
    this.listeners = new Set(); // Event listeners for updates
    this.lastRefresh = 0; // Timestamp of last data refresh
    this.refreshInterval = 5 * 60 * 1000; // 5 minutes
    this.globalLoadingState = new Map(); // Track global loading state per URL
    this.instanceCoordinators = new Map(); // Coordinate between instances for the same coin
  }

  /**
   * Add a listener for icon updates
   * @param {Function} listener - The listener function to call when icons are updated
   */
  addListener(listener) {
    this.listeners.add(listener);
  }

  /**
   * Remove a listener
   * @param {Function} listener - The listener function to remove
   */
  removeListener(listener) {
    this.listeners.delete(listener);
  }

  /**
   * Notify all listeners that icons have been updated
   */
  notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.warn('[IconService] Error in listener:', error);
      }
    });
  }

  /**
   * Get all available icon URLs for a coin in order of preference
   * @param {string} coinSymbol - The coin symbol (e.g., 'BTC', 'ETH')
   * @returns {Array<string>} - Array of available URLs
   */
  getAvailableIconUrls(coinSymbol) {
    if (!coinSymbol) return [];
    
    // Handle symbol mapping for icon lookup (same as chart data mapping)
    let symbolToLookup = coinSymbol;
    if (coinSymbol === 'Bonk') {
      symbolToLookup = 'BONK';
    } else if (coinSymbol === '$WIF') {
      symbolToLookup = 'WIF';
    }
    
    const coin = this.coinDataCache.get(symbolToLookup);
    if (!coin) return [];
    
    // Return all available URLs in order of preference
    return [coin.image_url, coin.image_url_2, coin.image_url_3].filter(Boolean);
  }

  /**
   * Get the best available icon URL for a coin
   * @param {string} coinSymbol - The coin symbol (e.g., 'BTC', 'ETH')
   * @returns {string|null} - The best available icon URL or null
   */
  getBestIconUrl(coinSymbol) {
    if (!coinSymbol) return null;
    
    // Handle symbol mapping for icon lookup (same as chart data mapping)
    let symbolToLookup = coinSymbol;
    if (coinSymbol === 'Bonk') {
      symbolToLookup = 'BONK';
    } else if (coinSymbol === '$WIF') {
      symbolToLookup = 'WIF';
    }
    
    const coin = this.coinDataCache.get(symbolToLookup);
    if (!coin) return null;
    
    // Get all available URLs for this coin
    const urls = [coin.image_url, coin.image_url_2, coin.image_url_3].filter(Boolean);
    
    if (urls.length === 0) return null;
    
    // Try to find the first URL that is either:
    // 1. Already loaded successfully
    // 2. Currently loading
    // 3. Not failed yet
    for (const url of urls) {
      if (this.iconCache.has(url)) {
        return url; // Already loaded successfully
      }
    }
    
    for (const url of urls) {
      if (this.loadingIcons.has(url)) {
        return url; // Currently loading
      }
    }
    
    for (const url of urls) {
      if (!this.failedIcons.has(url)) {
        return url; // Not failed yet, try this one
      }
    }
    
    // If all URLs have failed, return the first one anyway (might work on retry)
    return urls[0] || null;
  }

  /**
   * Check if an icon is currently loading
   * @param {string} iconUrl - The icon URL to check
   * @returns {boolean} - True if loading
   */
  isIconLoading(iconUrl) {
    return this.loadingIcons.has(iconUrl);
  }

  /**
   * Check if an icon has been loaded successfully
   * @param {string} iconUrl - The icon URL to check
   * @returns {boolean} - True if loaded
   */
  isIconLoaded(iconUrl) {
    return this.iconCache.has(iconUrl);
  }

  /**
   * Check if an icon has failed to load
   * @param {string} iconUrl - The icon URL to check
   * @returns {boolean} - True if failed
   */
  isIconFailed(iconUrl) {
    return this.failedIcons.has(iconUrl);
  }

  /**
   * Load an image with timeout and error handling
   * @param {string} url - The image URL to load
   * @returns {Promise<boolean>} - Whether the image was loaded successfully
   */
  async loadImage(url) {
    return new Promise((resolve) => {
      // Create a new Image object
      const img = new Image();
      
      // Set up event handlers
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      
      // Set a timeout to prevent hanging (reduced from 10s to 5s for faster fallbacks)
      const timeout = setTimeout(() => resolve(false), 5000);
      
      // Start loading the image
      img.src = url;
      
      // Clean up timeout when image loads or fails
      img.onload = () => {
        clearTimeout(timeout);
        resolve(true);
      };
      
      img.onerror = () => {
        clearTimeout(timeout);
        resolve(false);
      };
    });
  }

  /**
   * Load an icon and cache it
   * @param {string} url - The icon URL to load
   * @returns {Promise<boolean>} - Whether the icon was loaded successfully
   */
  async loadIcon(url) {
    if (!url) return false;
    
    // If already loaded, return success
    if (this.iconCache.has(url)) {
      return true;
    }
    
    // If permanently failed, don't retry
    if (this.failedIcons.has(url)) {
      return false;
    }
    
    // If currently loading, wait for it to complete
    if (this.loadingIcons.has(url)) {
      return new Promise((resolve) => {
        const checkLoading = () => {
          if (this.iconCache.has(url)) {
            resolve(true);
          } else if (this.failedIcons.has(url)) {
            resolve(false);
          } else {
            setTimeout(checkLoading, 100);
          }
        };
        checkLoading();
      });
    }
    
    // Mark as loading
    this.loadingIcons.add(url);
    
    try {
      // console.log(`[IconService] Loading icon: ${url}`);
      
      const success = await this.loadImage(url);
      
      if (success) {
        this.iconCache.add(url);
        // console.log(`[IconService] ✅ Successfully loaded icon: ${url}`);
        this.notifyListeners();
        return true;
      } else {
        this.failedIcons.add(url);
        // console.log(`[IconService] ❌ Failed to load icon: ${url} Image load timeout`);
        return false;
      }
    } catch (error) {
      this.failedIcons.add(url);
      // console.log(`[IconService] ❌ Failed to load icon: ${url} ${error.message}`);
      return false;
    } finally {
      this.loadingIcons.delete(url);
    }
  }

  /**
   * Test if a URL is accessible
   * @param {string} url - The URL to test
   * @returns {Promise<boolean>} - True if accessible
   */
  async testUrlAccessibility(url) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      
      const response = await fetch(url, { 
        method: 'HEAD',
        signal: controller.signal,
        mode: 'no-cors'
      });
      
      clearTimeout(timeoutId);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Load all coin data from the server
   * @returns {Promise<void>}
   */
  async loadCoinData() {
    try {
      const response = await api.get('/api/coins');
      const coins = response.data || [];
      
      // Clear and rebuild cache
      this.coinDataCache.clear();
      coins.forEach(coin => {
        this.coinDataCache.set(coin.symbol, coin);
      });
      
      this.lastRefresh = Date.now();
      // console.log(`[IconService] Loaded ${coins.length} coins from server`);
      
      // Notify listeners that coin data has been updated
      this.notifyListeners();
    } catch (error) {
      console.error('[IconService] Error loading coin data:', error);
    }
  }

  /**
   * Refresh coin data if needed
   * @returns {Promise<void>}
   */
  async refreshCoinDataIfNeeded() {
    const now = Date.now();
    if (!this.lastRefresh || (now - this.lastRefresh) > this.refreshInterval) {
      await this.loadCoinData();
    }
  }

  /**
   * Load all available icons for coins
   * @returns {Promise<void>}
   */
  async loadAllIcons() {
    await this.refreshCoinDataIfNeeded();
    
    const allIconUrls = new Set();
    this.coinDataCache.forEach(coin => {
      const urls = this.getAvailableIconUrls(coin.symbol);
      urls.forEach(url => allIconUrls.add(url));
    });

    const urls = Array.from(allIconUrls);
    // console.log(`[IconService] Loading ${urls.length} icons in batches...`);

    // Filter out URLs that are already loaded, loading, or failed
    const urlsToLoad = urls.filter(url => 
      !this.iconCache.has(url) && 
      !this.loadingIcons.has(url) && 
      !this.failedIcons.has(url)
    );

    if (urlsToLoad.length === 0) {
      // console.log(`[IconService] All icons already processed (${urls.length} total)`);
      return;
    }

    // console.log(`[IconService] Loading ${urlsToLoad.length} new icons in batches...`);

    // Load icons in batches to avoid overwhelming the browser
    const batchSize = 5;
    for (let i = 0; i < urlsToLoad.length; i += batchSize) {
      const batch = urlsToLoad.slice(i, i + batchSize);
      await Promise.allSettled(batch.map(url => this.loadIcon(url)));
      
      if (i + batchSize < urlsToLoad.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  }

  /**
   * Force refresh coin data and icons
   * @returns {Promise<void>}
   */
  async forceRefresh() {
    this.lastRefresh = null;
    this.iconCache.clear();
    this.failedIcons.clear();
    
    try {
      // First, trigger server-side icon refresh for missing icons
      // console.log('[IconService] Triggering server-side icon refresh...');
      await api.post('/api/coins/refresh-icons');
      // console.log('[IconService] Server-side icon refresh completed');
    } catch (error) {
      console.warn('[IconService] Server-side icon refresh failed:', error);
    }
    
    // Then load all icons locally
    await this.loadAllIcons();
  }

  /**
   * Refresh only new coins that might have been added during wallet sync
   * This is more efficient than forceRefresh as it doesn't clear existing caches
   * @returns {Promise<void>}
   */
  async refreshNewCoins() {
    try {
      // console.log('[IconService] Refreshing coin data to check for new coins...');
      
      // Get current coin count
      const currentCoinCount = this.coinDataCache.size;
      
      // Load fresh coin data from server
      await this.loadCoinData();
      
      const newCoinCount = this.coinDataCache.size;
      
      if (newCoinCount > currentCoinCount) {
        // console.log(`[IconService] Found ${newCoinCount - currentCoinCount} new coins, loading their icons...`);
        
        // Load only the new icons
        const allIconUrls = new Set();
        this.coinDataCache.forEach(coin => {
          if (coin.image_url) allIconUrls.add(coin.image_url);
          if (coin.image_url_2) allIconUrls.add(coin.image_url_2);
          if (coin.image_url_3) allIconUrls.add(coin.image_url_3);
        });

        const urls = Array.from(allIconUrls);
        // console.log(`[IconService] Loading ${urls.length} icons in batches...`);

        // Load icons in batches to avoid overwhelming the browser
        const batchSize = 5;
        for (let i = 0; i < urls.length; i += batchSize) {
          const batch = urls.slice(i, i + batchSize);
          await Promise.allSettled(batch.map(url => this.loadIcon(url)));
          
          if (i + batchSize < urls.length) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      } else {
        // console.log('[IconService] No new coins detected, skipping icon refresh');
      }
    } catch (error) {
      console.warn('[IconService] Error refreshing new coins:', error);
    }
  }

  /**
   * Refresh icons for specific coin symbols
   * @param {Array<string>} coinSymbols - Array of coin symbols to refresh
   * @returns {Promise<void>}
   */
  async refreshCoins(coinSymbols) {
    try {
      // console.log(`[IconService] Refreshing icons for specific coins: ${coinSymbols.join(', ')}`);
      
      // First, trigger server-side icon refresh for these specific coins
      try {
        // console.log('[IconService] Triggering server-side icon refresh for specific coins...');
        await api.post('/api/coins/refresh-icons');
        // console.log('[IconService] Server-side icon refresh completed');
      } catch (error) {
        console.warn('[IconService] Server-side icon refresh failed:', error);
      }
      
      // Reload coin data to get updated icon URLs
      await this.loadCoinData();
      
      // Load icons for the specified coins
      const iconUrls = new Set();
      coinSymbols.forEach(symbol => {
        const coin = this.coinDataCache.get(symbol);
        if (coin) {
          if (coin.image_url) iconUrls.add(coin.image_url);
          if (coin.image_url_2) iconUrls.add(coin.image_url_2);
          if (coin.image_url_3) iconUrls.add(coin.image_url_3);
        }
      });

      const urls = Array.from(iconUrls);
      // console.log(`[IconService] Loading ${urls.length} icons for specified coins...`);

      // Load icons in batches
      const batchSize = 5;
      for (let i = 0; i < urls.length; i += batchSize) {
        const batch = urls.slice(i, i + batchSize);
        await Promise.allSettled(batch.map(url => this.loadIcon(url)));
        
        if (i + batchSize < urls.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    } catch (error) {
      console.warn('[IconService] Error refreshing specific coins:', error);
    }
  }

  /**
   * Check if any coins are missing icons and refresh them if needed
   * @returns {Promise<void>}
   */
  async refreshMissingIcons() {
    try {
      // console.log('[IconService] Checking for coins with missing icons...');
      
      // Get current coin data
      await this.refreshCoinDataIfNeeded();
      
      // Find coins without icons
      const coinsWithoutIcons = [];
      this.coinDataCache.forEach(coin => {
        if (!coin.image_url && !coin.image_url_2 && !coin.image_url_3) {
          coinsWithoutIcons.push(coin.symbol);
        }
      });
      
      if (coinsWithoutIcons.length > 0) {
        // console.log(`[IconService] Found ${coinsWithoutIcons.length} coins without icons: ${coinsWithoutIcons.join(', ')}`);
        
        // Trigger server-side refresh for missing icons
        try {
          // console.log('[IconService] Triggering server-side icon refresh for missing icons...');
          await api.post('/api/coins/refresh-icons');
          // console.log('[IconService] Server-side icon refresh completed');
        } catch (error) {
          console.warn('[IconService] Server-side icon refresh failed:', error);
        }
        
        // Reload coin data to get updated icon URLs
        await this.loadCoinData();
        
        // Load the newly available icons
        const iconUrls = new Set();
        coinsWithoutIcons.forEach(symbol => {
          const coin = this.coinDataCache.get(symbol);
          if (coin) {
            if (coin.image_url) iconUrls.add(coin.image_url);
            if (coin.image_url_2) iconUrls.add(coin.image_url_2);
            if (coin.image_url_3) iconUrls.add(coin.image_url_3);
          }
        });

        const urls = Array.from(iconUrls);
        if (urls.length > 0) {
          // console.log(`[IconService] Loading ${urls.length} newly available icons...`);
          
          // Load icons in batches
          const batchSize = 5;
          for (let i = 0; i < urls.length; i += batchSize) {
            const batch = urls.slice(i, i + batchSize);
            await Promise.allSettled(batch.map(url => this.loadIcon(url)));
            
            if (i + batchSize < urls.length) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          }
        }
      } else {
        // console.log('[IconService] All coins have icons, no refresh needed');
      }
    } catch (error) {
      console.warn('[IconService] Error checking for missing icons:', error);
    }
  }

  /**
   * Get coin data for a specific symbol
   * @param {string} coinSymbol - The coin symbol
   * @returns {Object|null} - The coin data or null
   */
  getCoinData(coinSymbol) {
    // Handle symbol mapping for icon lookup (same as chart data mapping)
    let symbolToLookup = coinSymbol;
    if (coinSymbol === 'Bonk') {
      symbolToLookup = 'BONK';
    } else if (coinSymbol === '$WIF') {
      symbolToLookup = 'WIF';
    }
    
    return this.coinDataCache.get(symbolToLookup) || null;
  }

  /**
   * Get all coin data
   * @returns {Array} - Array of all coin data
   */
  getAllCoinData() {
    return Array.from(this.coinDataCache.values());
  }

  /**
   * Clear all caches
   */
  clearCaches() {
    this.iconCache.clear();
    this.loadingIcons.clear();
    this.failedIcons.clear();
    this.coinDataCache.clear();
    this.lastRefresh = null;
  }

  /**
   * Preload all available icons for a coin
   * @param {string} coinSymbol - The coin symbol (e.g., 'BTC', 'ETH')
   * @returns {Promise<void>}
   */
  async preloadCoinIcons(coinSymbol) {
    if (!coinSymbol) return;
    
    const urls = this.getAvailableIconUrls(coinSymbol);
    if (urls.length === 0) return;
    
    // Filter out URLs that are already processed
    const urlsToLoad = urls.filter(url => 
      !this.iconCache.has(url) && 
      !this.loadingIcons.has(url) && 
      !this.failedIcons.has(url)
    );
    
    if (urlsToLoad.length === 0) {
      return; // All URLs already processed
    }
    
    // console.log(`[IconService] Preloading ${urlsToLoad.length} icons for ${coinSymbol}`);
    
    // Load all URLs in parallel, but don't wait for all to complete
    urlsToLoad.forEach(url => {
      this.loadIcon(url).catch(error => {
        console.warn(`[IconService] Failed to preload icon for ${coinSymbol}: ${url}`, error.message);
      });
    });
  }

  /**
   * Clear failed icons for a coin to allow retrying
   * @param {string} coinSymbol - The coin symbol (e.g., 'BTC', 'ETH')
   */
  clearFailedIcons(coinSymbol) {
    if (!coinSymbol) return;
    
    const urls = this.getAvailableIconUrls(coinSymbol);
    urls.forEach(url => {
      this.failedIcons.delete(url);
    });
  }

  /**
   * Clear failed icon for a specific URL to allow retrying
   * @param {string} url - The specific URL to clear
   */
  clearFailedIcon(url) {
    if (!url) return;
    this.failedIcons.delete(url);
  }

  /**
   * Force retry a failed icon
   * @param {string} url - The URL to retry
   * @returns {Promise<boolean>} - Whether the retry was successful
   */
  async retryFailedIcon(url) {
    if (!url) return false;
    
    // Clear the failed status
    this.clearFailedIcon(url);
    
    // Try to load again
    return await this.loadIcon(url);
  }

  /**
   * Get the status of all URLs for a coin
   * @param {string} coinSymbol - The coin symbol (e.g., 'BTC', 'ETH')
   * @returns {Array<Object>} - Array of URL status objects
   */
  getCoinUrlStatus(coinSymbol) {
    if (!coinSymbol) return [];
    
    const urls = this.getAvailableIconUrls(coinSymbol);
    return urls.map(url => ({
      url,
      isLoaded: this.isIconLoaded(url),
      isLoading: this.isIconLoading(url),
      isFailed: this.isIconFailed(url)
    }));
  }

  /**
   * Get or create a coordinator for a specific coin
   * @param {string} coinSymbol - The coin symbol
   * @returns {Object} - The coordinator object
   */
  getCoordinator(coinSymbol) {
    if (!this.instanceCoordinators.has(coinSymbol)) {
      this.instanceCoordinators.set(coinSymbol, {
        loadingInstances: new Set(),
        loadedUrl: null,
        failedUrls: new Set(),
        isProcessing: false
      });
    }
    return this.instanceCoordinators.get(coinSymbol);
  }

  /**
   * Register an instance for a coin
   * @param {string} coinSymbol - The coin symbol
   * @param {string} instanceId - The instance ID
   */
  registerInstance(coinSymbol, instanceId) {
    const coordinator = this.getCoordinator(coinSymbol);
    coordinator.loadingInstances.add(instanceId);
  }

  /**
   * Unregister an instance for a coin
   * @param {string} coinSymbol - The coin symbol
   * @param {string} instanceId - The instance ID
   */
  unregisterInstance(coinSymbol, instanceId) {
    const coordinator = this.getCoordinator(coinSymbol);
    coordinator.loadingInstances.delete(instanceId);
    
    // Clean up coordinator if no instances are using it
    if (coordinator.loadingInstances.size === 0) {
      this.instanceCoordinators.delete(coinSymbol);
    }
  }

  /**
   * Check if any instance is currently processing a coin
   * @param {string} coinSymbol - The coin symbol
   * @returns {boolean} - Whether any instance is processing
   */
  isCoinBeingProcessed(coinSymbol) {
    const coordinator = this.getCoordinator(coinSymbol);
    return coordinator.isProcessing;
  }

  /**
   * Mark a coin as being processed
   * @param {string} coinSymbol - The coin symbol
   * @param {boolean} isProcessing - Whether it's being processed
   */
  setCoinProcessing(coinSymbol, isProcessing) {
    const coordinator = this.getCoordinator(coinSymbol);
    coordinator.isProcessing = isProcessing;
  }
}

// Create a singleton instance
const iconService = new IconService();

export default iconService; 